import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { ApiRequest } from '../../../../types/admin';

interface RequestViewerProps {
  request: ApiRequest | null;
}

export const RequestViewer: React.FC<RequestViewerProps> = ({ request }) => {
  if (!request) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">
            No request data available. Execute an API call to see request details.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET':
        return 'primary';
      case 'POST':
        return 'success';
      case 'PUT':
        return 'warning';
      case 'DELETE':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h6">Request</Typography>
          <Chip
            label={request.method}
            color={getMethodColor(request.method)}
            size="small"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sent at: {new Date(request.timestamp).toLocaleString()}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
            URL:
          </Typography>
          <Box
            sx={{
              backgroundColor: '#f5f5f5',
              p: 1,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              wordBreak: 'break-all',
            }}
          >
            {request.url}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">Request Headers</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              component="pre"
              sx={{
                backgroundColor: '#f5f5f5',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                maxHeight: 200,
              }}
            >
              {JSON.stringify(request.headers, null, 2)}
            </Box>
          </AccordionDetails>
        </Accordion>

        {request.body && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Request Body</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  backgroundColor: '#f5f5f5',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  maxHeight: 400,
                }}
              >
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(request.body), null, 2);
                  } catch {
                    return request.body;
                  }
                })()}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
};